/**
 * The 44px column at the end of a service row.
 *
 * Only entries that do something. Delete is offered for a service that can
 * actually be deleted and left off the platform service entirely: the API
 * refuses that one with `platform_product_protected`, so the entry would be a
 * button that always fails, teaching the rule through an error message instead
 * of through the menu. The other refusal - a service that still has packages -
 * cannot be predicted from the row, so that one stays and surfaces its message
 * in the confirm dialog.
 */
import { ArrowUpRight, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { makeT, type Locale, type ProductDTO } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { Pop, useClosePops } from "../../../shell/Pop";

export function ServiceRowMenu({
  product,
  locale,
  onOpen,
  onEdit,
  onDelete,
}: {
  product: ProductDTO;
  locale: Locale;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  // Two dictionaries, on purpose. The menu ENTRIES name things the plan
  // catalogue owns, so they come from the shared `@wa/ui` strings that web_admin
  // reads too; the trigger's label is CRM chrome and comes from the CRM's own.
  const t = makeT(locale);
  const ct = useT();
  const close = useClosePops();

  /** Close first, act second: every one of these either navigates away or opens
   *  a dialog over the top, and a menu left hanging behind a modal is a menu the
   *  operator has to dismiss twice. */
  const run = (fn: () => void) => () => {
    close();
    fn();
  };

  return (
    // Above the row-covering link overlay, or the trigger never gets the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={ct("common.more")}
            aria-label={ct("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button type="button" className="crm-mi" onClick={run(onOpen)}>
          <ArrowUpRight className="size-4" />
          {t("services.openService")}
        </button>
        <button type="button" className="crm-mi" onClick={run(onEdit)}>
          <Pencil className="size-4" />
          {t("services.editService")}
        </button>
        {product.isPlatform ? null : (
          <>
            <div className="crm-menu-sep" />
            <button type="button" className="crm-mi" onClick={run(onDelete)}>
              <Trash2 className="size-4" />
              {t("services.deleteService")}
            </button>
          </>
        )}
      </Pop>
    </span>
  );
}
